import { useState, useEffect, useRef } from "react";
import { Checkbox, Input } from "antd";
import IMAGES from "../../../../constant/images";
import MemberFilterDrawer from "./MemberFilterDrawer";
import { Search, Filter, ArrowUpDown } from "lucide-react";

export default function Index({
  onSearchChange,
  onSortChange,
  filters,
  setFilters,
  setCurrentPage,
  selectedRows,
  setSelectedRows,
  exportData,
  setExportData
}) {
  const [open1, setOpen1] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const dropdownRef = useRef();
  const mobileDropdownRef = useRef();
  const [searchValue, setSearchValue] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
const [selectAll, setSelectAll] = useState(false); // Add this line at the top of your component

  // ✅ Active filter count (excluding sort fields)
  const activeFilterCount = Object.entries(filters || {}).filter(
    ([key, value]) => {
      // Exclude sort fields from filter count
      if (key === 'sortField' || key === 'sortOrder') return false;
      
      return value !== null &&
             value !== "" &&
             value !== "none" &&
             !(Array.isArray(value) && value.length === 0);
    }
  ).length;

  const hasActiveFilters = activeFilterCount > 0;

  // ✅ Detect active sorting
  const hasActiveSorting = Boolean(
    filters?.sortField && 
    filters?.sortOrder && 
    filters?.sortOrder !== "none" &&
    filters?.sortField !== "none"
  );

  const activeSortingCount = hasActiveSorting ? 1 : 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          mobileDropdownRef.current && !mobileDropdownRef.current.contains(e.target)) {
        setOpen1(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Call search callback when typing (debounced)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      onSearchChange?.(searchValue.trim());
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchValue, onSearchChange]);

  // ✅ Fixed sort handler
  const handleSortChange = (field, order) => {
    console.log("Sorting:", { field, order }); // Debug log
    
    // Reset to page 1 when sorting
    setCurrentPage?.(1);
    
    // Update filters state with sort information
    setFilters?.(prev => ({
      ...prev,
      sortField: field,
      sortOrder: order
    }));
    
    // Call the sort change callback
    onSortChange?.({ field, order });
    
    // Close dropdown
    setOpen1(false);
  };

  // ✅ Clear sorting handler
  const handleClearSort = () => {
    console.log("Clearing sort"); // Debug log
    
    setCurrentPage?.(1);
    
    setFilters?.(prev => ({
      ...prev,
      sortField: null,
      sortOrder: null
    }));
    
    onSortChange?.({ field: null, order: null });
    setOpen1(false);
  };

  // ✅ Check if current sort option is active
  const isCurrentSort = (field, order) => {
    return filters?.sortField === field && filters?.sortOrder === order;
  };
  
    // ✅ Select All logic
  const allSelected = exportData && selectedRows.length === exportData.length && exportData.length > 0;
  const partiallySelected = selectedRows.length > 0 && selectedRows.length < exportData.length;

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedRows(exportData.map((item) => item.key));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 p-2">
        {/* Title */}
        <div className="flex items-center">
          <p className="text-base font-semibold text-[#171717] font-poppins ml-2 mb-0">
            All Members
          </p>
        </div>

        {/* Desktop Controls */}
        <div className="hidden md:flex items-center gap-3">
          {/* Search Input */}
          <div className="w-[360px]">
            <Input
              type="text"
              placeholder="Search by name"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full h-9 px-3 rounded-xl border-[1px] border-solid border-[#E2E8F0]
                text-[#171717] bg-white shadow-sm font-normal text-sm font-poppins focus:ring-2 focus:ring-[#B3D7FF]
                placeholder:font-normal placeholder:text-sm placeholder:text-[#94A3B8]"
              prefix={<Search size={16} className="text-[#64748B] mr-1" />}
            />
          </div>

          {/* Filter Button */}
          <button
            className="relative flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition font-poppins"
            onClick={() => setFilterOpen(true)}
          >
            Filter
            <Filter size={14} className="text-[#2563EB]" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Sort Dropdown - Desktop */}
          <div className="relative inline-block" ref={dropdownRef}>
            <button
              onClick={() => setOpen1(!open1)}
              className="relative flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition font-poppins"
            >
              <ArrowUpDown size={14} className="text-[#171717]" />
              {hasActiveSorting && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {activeSortingCount}
                </span>
              )}
            </button>
            {open1 && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-lg border-[1px] border-solid border-[#E2E8F0] py-2 z-50 font-poppins text-sm text-[#334155]">
                {/* Clear Sort Option */}
                {hasActiveSorting && (
                  <div
                    className="px-4 py-3 hover:bg-[#F8FAFC] cursor-pointer border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] text-[#64748B] font-medium"
                    onClick={handleClearSort}
                  >
                    ✕ Clear Sorting
                  </div>
                )}
                
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("name", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold border-l-4 border-[#2563EB]"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "asc")}
                >
                  {isCurrentSort("name", "asc") && "✓ "} Sort by Name (A-Z)
                </div>
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("name", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold border-l-4 border-[#2563EB]"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "desc")}
                >
                  {isCurrentSort("name", "desc") && "✓ "} Sort by Name (Z-A)
                </div>
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("joinedSince", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold border-l-4 border-[#2563EB]"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("joinedSince", "asc")}
                >
                  {isCurrentSort("joinedSince", "asc") && "✓ "} Sort by Joined (Oldest)
                </div>
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("joinedSince", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold border-l-4 border-[#2563EB]"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("joinedSince", "desc")}
                >
                  {isCurrentSort("joinedSince", "desc") && "✓ "} Sort by Joined (Newest)
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Controls */}
        <div className="flex md:hidden items-center gap-3">
          {/* Mobile Search Button */}
          <img
            src={IMAGES.mobile_searching_icon}
            alt="search"
            width={40}
            height={40}
            className="cursor-pointer"
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
          />

          {/* Mobile Filter Button */}
          <div className="relative">
            <img
              src={IMAGES.mobile_filter_icon}
              alt="filter"
              width={40}
              height={40}
              className="cursor-pointer"
              onClick={() => setFilterOpen(true)}
            />
            {hasActiveFilters && (
              <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>

          {/* Mobile Sort Button */}
          <div className="relative" ref={mobileDropdownRef}>
            <img
              src={IMAGES.mobile_sorting_icon}
              alt="sorting"
              width={40}
              height={40}
              className="cursor-pointer"
              onClick={() => setOpen1(!open1)}
            />
            {hasActiveSorting && (
              <span className="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                {activeSortingCount}
              </span>
            )}
            {open1 && (
              <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-lg border-[1px] border-solid border-[#E2E8F0] py-1 z-50 font-poppins text-xs text-[#334155]">
                {/* Clear Sort Option - Mobile */}
                {hasActiveSorting && (
                  <div
                    className="px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] text-[#64748B] font-medium"
                    onClick={handleClearSort}
                  >
                    ✕ Clear
                  </div>
                )}
                
                <div
                  className={`px-3 py-2 cursor-pointer ${
                    isCurrentSort("name", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "asc")}
                >
                  {isCurrentSort("name", "asc") && "✓ "} Name (A-Z)
                </div>
                <div
                  className={`px-3 py-2 cursor-pointer ${
                    isCurrentSort("name", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "desc")}
                >
                  {isCurrentSort("name", "desc") && "✓ "} Name (Z-A)
                </div>
                <div
                  className={`px-3 py-2 cursor-pointer ${
                    isCurrentSort("joinedSince", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("joinedSince", "asc")}
                >
                  {isCurrentSort("joinedSince", "asc") && "✓ "} Joined (Old)
                </div>
                <div
                  className={`px-3 py-2 cursor-pointer ${
                    isCurrentSort("joinedSince", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("joinedSince", "desc")}
                >
                  {isCurrentSort("joinedSince", "desc") && "✓ "} Joined (New)
                </div>
              </div>
            )}
          </div>

          {/* Select All Checkbox */}
          <Checkbox
            className="flex-shrink-0"
             checked={allSelected}
            indeterminate={partiallySelected}
            onChange={handleSelectAll}
          />
        </div>

        {/* ✅ Mobile Search Bar */}
        {mobileSearchOpen && (
          <div className="w-full p-2">
            <Input
              type="text"
              placeholder="Search by name"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full h-9 px-3 rounded-xl border-[1px] border-solid border-[#E2E8F0]
                text-[#171717] bg-white shadow-sm font-normal text-sm font-poppins focus:ring-2 focus:ring-[#B3D7FF]
                placeholder:font-normal placeholder:text-sm placeholder:text-[#94A3B8]"
              prefix={<Search size={16} className="text-[#64748B] mr-1" />}
            />
          </div>
        )}
      </div>

      {/* ✅ Member Filter Drawer */}
      <MemberFilterDrawer
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        filters={filters}
        setFilters={setFilters}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}